import type { FC } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import { DollarIcon, FocusIcon, MobileIcon } from '@segment/matcha/components/Icons/Brandbook'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import OptimizeTheFirstTimeUserExperienceImage from './OptimizeTheFirstTimeUserExperienceImage'

const copy = {
  heading: `Optimize the first-time user experience`,
  description: `Collect and analyze data from all customer touch points to identify areas where users get stuck. Brainstorm experiments to improve conversions and test your results.`,
}

const UnifyYourBuyer: FC = () => {
  const theme = useTheme()
  const list = [
    {
      title: `Deliver personalized experiences across channels`,
      description: `Tailor your apps and websites with full context of every customer’s interaction with your brand: support, purchases, email engagement, surveys, and more.`,
      icon: <MobileIcon size="small" color={theme.palette.brand.darkAcai[700]} />,
    },
    {
      title: `Understand attribution across channels`,
      description: `Drive down the cost of your marketing by analyzing lifetime value of customers per channel. Layer in support to find hidden costs.`,
      icon: <DollarIcon size="small" color={theme.palette.brand.darkAcai[700]} />,
    },
    {
      title: `Get all of your data into one place`,
      description: `Use a single system to manage data and integrations across your apps that target service providers and those that cater to end buyers.`,
      icon: <FocusIcon size="small" color={theme.palette.brand.darkAcai[700]} />,
    },
  ]
  return (
    <Wrapper>
      <SectionBase variant="2Col" reverse={true}>
        <SectionCopy>
          <CopyTextBase>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.description}
            </CopyText.Description>
          </CopyTextBase>
        </SectionCopy>

        <Section.Contents>
          <OptimizeTheFirstTimeUserExperienceImage />
        </Section.Contents>
      </SectionBase>
      <SectionBase variant="noCopy">
        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ md: 3 }}>
            {list.map(({ title, description, icon }, index) => (
              <FeatureBoxBase key={index} headerPosition="top" alignment="left">
                <FeatureBoxHeader>{icon}</FeatureBoxHeader>
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text>{description}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </StyledColumnTrack>
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  margin-top: -40px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-top: -80px;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-left: 80px;
    padding-right: 80px;
  }
`

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 392px;
    justify-self: end;
    align-self: center;
  }
`

const FeatureBoxHeader = styled(FeatureBox.Header)`
  span > svg {
    width: 30px;
    height: 30px;
  }
`

export default UnifyYourBuyer
